import {
  applyMiddleware,
  createStore,
  combineReducers,
  type Middleware,
  compose,
} from "redux";
import { entitiesReducer, queriesReducer, queryMiddleware } from "redux-query";
import superagentInterface from "redux-query-interface-superagent";
import type { RootState } from "StoreTypes";
import { navMenuReducer } from "ui-modules/nav/navSlice";
import { modalReducer } from "ui-modules/modals/modalSlice";
import { userReducer } from "modules/user/userSlice";
import { facilityReducer } from "ui-modules/facility-control/facilityControlSlice";
import { signalRMiddleware } from "utils/websockets/websocketMiddleware";
import { simulatorReservationReducer } from "ui-modules/simulator-reservation/simulatorReservationSlice";
import { eventReducer } from "ui-modules/event/eventSlice";
import { queueReducer } from "ui-modules/queue/queueSlice";
import { zodValidationMiddleware } from "./zodValidationMiddleware";
import { config } from "@/config/env";
import { leaguesV2Api } from "features/leagues-v2/api/leaguesV2Api";
import { leaguesV2TrackingMiddleware } from "features/leagues-v2/analytics/leaguesV2TrackingMiddleware";
import { leaguesV2Reducer } from "features/leagues-v2/store/leaguesV2Slice";
import { clubhouseApi } from "@/services/clubhouseApi";
import { competitionsApi } from "features/competitions/api/competitionsApi";
import { adkitApi } from "features/adkit/api/adkitApi";

export const getQueries = (state: RootState) => state.queries;
export const getEntities = (state: RootState) => state.entities;

const enableDevtools = config.enableDevTools === "true";

// Create combined reducer using our custom entities reducer
const rootReducer = combineReducers({
  entities: entitiesReducer,
  queries: queriesReducer,
  nav: navMenuReducer,
  modals: modalReducer,
  user: userReducer,
  facility: facilityReducer,
  simulatorReservation: simulatorReservationReducer,
  queue: queueReducer,
  event: eventReducer,
  leaguesV2: leaguesV2Reducer,
  [leaguesV2Api.reducerPath]: leaguesV2Api.reducer,
  [clubhouseApi.reducerPath]: clubhouseApi.reducer,
  [competitionsApi.reducerPath]: competitionsApi.reducer,
  [adkitApi.reducerPath]: adkitApi.reducer,
});

// Simple logging middleware
const loggingMiddleware: Middleware = () => (next) => (action) => {
  // Let's log all actions in development mode
  if (process.env.NODE_ENV !== "production") {
    console.debug(`📊 DISPATCHING: ${action.type}`);
  }

  const result = next(action);

  return result;
};

const middlewares: Middleware[] = [
  loggingMiddleware, // Add this first to log all actions
  zodValidationMiddleware,
  queryMiddleware(superagentInterface, getQueries, getEntities),
  signalRMiddleware,
  leaguesV2Api.middleware as Middleware,
  leaguesV2TrackingMiddleware,
  clubhouseApi.middleware as Middleware,
  competitionsApi.middleware as Middleware,
  adkitApi.middleware as Middleware,
];

const middlewareEnhancer = applyMiddleware(...middlewares);

// ---- Compose Enhancers ----
// Uses browser Redux DevTools extension only if:
//   1. VITE_ENABLE_DEVTOOLS=true
//   2. Browser extension is installed
const composeEnhancers =
  (enableDevtools && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;

// ---- Store ----
export const store = createStore(
  rootReducer,
  composeEnhancers(middlewareEnhancer),
);

export default store;
